import React from 'react';
import {
  Alert, Button, LinearProgress, Paper, Stack, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FactCheckIcon from '@mui/icons-material/FactCheck';

type Props = {
  title?: string;
  description?: string;
  accept?: string;
  disabled?: boolean;
  maxFileSizeBytes?: number;
  onDownloadTemplate: () => Promise<void>;
  onValidate: (file: File) => Promise<any>;
  onUpload: (file: File) => Promise<any>;
  onCompleted?: (result: any) => void;
};

type State = {
  file: File | null;
  validating: boolean;
  uploading: boolean;
  validationResult: any;
  error: string;
  success: string;
};

class FWorkbookImport extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    title: 'Excel Import',
    description: 'Download the template, complete it, then validate and import the workbook.',
    accept: '.xlsx,.xls',
    maxFileSizeBytes: 15 * 1024 * 1024,
  };

  state: State = {
    file: null, validating: false, uploading: false,
    validationResult: null, error: '', success: '',
  };

  private fileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) return this.setState({ file: null });

    if (this.props.maxFileSizeBytes && file.size > this.props.maxFileSizeBytes) {
      this.setState({
        file: null,
        error: 'The selected workbook is larger than the permitted file size.',
        success: '',
      });
      return;
    }

    this.setState({
      file, validationResult: null, error: '', success: '',
    });
  };

  private validate = async () => {
    if (!this.state.file) return;
    this.setState({ validating: true, error: '', success: '' });

    try {
      const validationResult = await this.props.onValidate(this.state.file);
      this.setState({
        validationResult,
        success: validationResult?.ready === false ? '' : 'Workbook validation completed.',
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ validating: false });
    }
  };

  private upload = async () => {
    if (!this.state.file) return;
    this.setState({ uploading: true, error: '', success: '' });

    try {
      const result = await this.props.onUpload(this.state.file);
      this.setState({ success: 'Workbook imported successfully.' });
      this.props.onCompleted?.(result);
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ uploading: false });
    }
  };

  render() {
    const busy = this.state.validating || this.state.uploading;

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <div>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{this.props.title}</Typography>
            <Typography variant="body2" color="text.secondary">{this.props.description}</Typography>
          </div>

          {busy && <LinearProgress />}
          {this.state.error && <Alert severity="error">{this.state.error}</Alert>}
          {this.state.success && <Alert severity="success">{this.state.success}</Alert>}
          {this.state.file && (
            <Alert severity="info">
              Selected file: <strong>{this.state.file.name}</strong>
            </Alert>
          )}
          {this.state.validationResult && (
            <Alert severity={this.state.validationResult?.ready === false ? 'warning' : 'success'}>
              {this.state.validationResult?.message || 'Validation completed.'}
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" startIcon={<DownloadIcon />}
              onClick={() => void this.props.onDownloadTemplate()}
              disabled={busy || this.props.disabled}>
              Download Template
            </Button>

            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}
              disabled={busy || this.props.disabled}>
              Choose Excel File
              <input hidden type="file" accept={this.props.accept} onChange={this.fileSelected} />
            </Button>

            <Button variant="outlined" startIcon={<FactCheckIcon />}
              onClick={this.validate}
              disabled={busy || this.props.disabled || !this.state.file}>
              Validate
            </Button>

            <Button variant="contained" startIcon={<UploadFileIcon />}
              onClick={this.upload}
              disabled={
                busy || this.props.disabled || !this.state.file ||
                this.state.validationResult?.ready === false
              }>
              Import Workbook
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }
}

export default FWorkbookImport;
